import { buildTradeReviewItems } from './mailboxData';
import type { MailboxDisplay, TradeGridItem } from './types';

type TradeReviewItems = { myItems: TradeGridItem[]; theirItems: TradeGridItem[] };
type Direction = 'incoming' | 'outgoing';

type TradeReviewManagerOptions = {
    fetchInventoryByPlayerId: (playerId: string) => Promise<Array<{ itemId: string; quantity: number; metadataJson: string }>>;
    markMailRead: (mailId: number) => Promise<void>;
    onChanged: () => void;
};

export type TradeReviewState = {
    incomingTradeReview: MailboxDisplay | null;
    incomingTradeReviewItems: TradeReviewItems | null;
    outgoingTradeReview: MailboxDisplay | null;
    outgoingTradeReviewItems: TradeReviewItems | null;
};

export type TradeReviewManager = {
    getState: () => TradeReviewState;
    clearForMailboxTab: (tab: 'Messages' | 'IncomingTrades' | 'OutgoingTrades') => void;
    close: (direction: Direction) => void;
    openIncoming: (mail: MailboxDisplay) => Promise<void>;
    openOutgoing: (mail: MailboxDisplay) => void;
};

export function createTradeReviewManager(options: TradeReviewManagerOptions): TradeReviewManager {
    const { fetchInventoryByPlayerId, markMailRead, onChanged } = options;
    let incomingTradeReview: MailboxDisplay | null = null;
    let incomingTradeReviewItems: TradeReviewItems | null = null;
    let incomingTradeReviewLoadVersion = 0;
    let outgoingTradeReview: MailboxDisplay | null = null;
    let outgoingTradeReviewItems: TradeReviewItems | null = null;
    let outgoingTradeReviewLoadVersion = 0;

    const getState = (): TradeReviewState => ({
        incomingTradeReview,
        incomingTradeReviewItems,
        outgoingTradeReview,
        outgoingTradeReviewItems,
    });

    const close = (direction: Direction): void => {
        if (direction === 'incoming') {
            incomingTradeReview = null;
            incomingTradeReviewItems = null;
        } else {
            outgoingTradeReview = null;
            outgoingTradeReviewItems = null;
        }
        onChanged();
    };

    const clearForMailboxTab = (tab: 'Messages' | 'IncomingTrades' | 'OutgoingTrades'): void => {
        if (tab !== 'IncomingTrades') {
            incomingTradeReview = null;
            incomingTradeReviewItems = null;
        }
        if (tab !== 'OutgoingTrades') {
            outgoingTradeReview = null;
            outgoingTradeReviewItems = null;
        }
    };

    const loadTradeReviewItems = async (
        mail: MailboxDisplay,
        direction: Direction,
        version: number,
        getCurrentVersion: () => number
    ): Promise<TradeReviewItems | null | undefined> => {
        try {
            const items = await buildTradeReviewItems(mail, direction, fetchInventoryByPlayerId);
            if (version !== getCurrentVersion()) return undefined;
            return items;
        } catch {
            if (version !== getCurrentVersion()) return undefined;
            return null;
        }
    };

    const openIncoming = async (mail: MailboxDisplay): Promise<void> => {
        const version = ++incomingTradeReviewLoadVersion;
        const items = await loadTradeReviewItems(mail, 'incoming', version, () => incomingTradeReviewLoadVersion);
        if (items === undefined) return;
        incomingTradeReviewItems = items;
        incomingTradeReview = mail;
        if (!mail.read) {
            try {
                await markMailRead(mail.mailId);
            } catch {
                // Non-fatal: still allow review modal.
            }
        }
        onChanged();
    };

    const openOutgoing = (mail: MailboxDisplay): void => {
        const version = ++outgoingTradeReviewLoadVersion;
        void (async () => {
            const items = await loadTradeReviewItems(mail, 'outgoing', version, () => outgoingTradeReviewLoadVersion);
            if (items === undefined) return;
            outgoingTradeReviewItems = items;
            outgoingTradeReview = mail;
            onChanged();
        })();
    };

    return {
        getState,
        clearForMailboxTab,
        close,
        openIncoming,
        openOutgoing,
    };
}
